import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchSingleConversationService } from "../../services/conversationServices";
import {
  ACTIVE_CONVERSATION_QUERY_KEY,
  createEmptyConversation,
} from "./constants";

/**
 * Reads the active conversation for the current route.
 *
 * Owns the React Query call and exposes `setActiveConversation` for
 * imperative cache writes (used by `useNewConversation` and the
 * regenerate flow). Conversation-fetch errors flow back via React
 * Query's `error` field; the composition root reduces them into the
 * chat state machine.
 */
export function useActiveConversation({ conversationId, clientId }) {
  const queryClient = useQueryClient();

  const {
    data: activeConversation = createEmptyConversation(),
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: [ACTIVE_CONVERSATION_QUERY_KEY, conversationId],
    enabled: Boolean(conversationId && clientId),
    placeholderData: createEmptyConversation,
    staleTime: Infinity,
    queryFn: () => {
      if (!conversationId) return undefined;
      return fetchSingleConversationService(clientId, conversationId);
    },
  });

  const setActiveConversation = useCallback(
    (conversation) => {
      return queryClient.setQueryData(
        [ACTIVE_CONVERSATION_QUERY_KEY, conversation.id],
        conversation,
      );
    },
    [queryClient],
  );

  return {
    activeConversation,
    setActiveConversation,
    isLoading,
    isFetching,
    error,
  };
}
